import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  FiBarChart2,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
} from "react-icons/fi";
import { useSidebar } from "./SidebarContext";
import { useAdminRole } from "../../hooks/useAdminRole";
import { useSupportStats } from "../../hooks/useSupportStats";
import { useModerationStats } from "../../hooks/useModerationStats";
import { AdminRole } from "../../types";
import { navGroups, NavItem, NavGroup } from "../../config/adminNav";
import logo from "../../assets/images/logo.png";

// ── Shared sub-item list renderer ──────────────────────────────────────────
function SubItems({
  items,
  hasRole,
  badges = {},
}: {
  items: NavItem[];
  hasRole: (r: AdminRole) => boolean;
  badges?: Record<string, number>;
}) {
  return (
    <div className="mt-0.5 ml-3 pl-3 border-l border-white/10 space-y-0.5">
      {items.map(({ to, label, Icon, minRole }) =>
        hasRole(minRole) ? (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm transition-all duration-150
              ${isActive ? "bg-magenta/15 text-magenta font-semibold" : "text-offwhite/50 hover:text-offwhite hover:bg-white/5"}`
            }
          >
            <Icon className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="flex items-center gap-1.5 flex-1 min-w-0">
              <span className="truncate">{label}</span>
              {(badges[to] ?? 0) > 0 && (
                <span className="text-xs font-bold min-w-[22px] text-center px-1.5 py-0.5 rounded-md bg-yellow/25 text-yellow leading-none">
                  {badges[to] > 99 ? "99+" : badges[to]}
                </span>
              )}
            </span>
          </NavLink>
        ) : (
          <div
            key={to}
            title="Insufficient permissions"
            className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm opacity-25 cursor-not-allowed"
          >
            <Icon className="w-3.5 h-3.5 flex-shrink-0" />
            {label}
          </div>
        ),
      )}
    </div>
  );
}

// ── Dropdown group ─────────────────────────────────────────────────────────
function NavGroupItem({
  group,
  collapsed,
  expandSidebar,
  hasRole,
  badges = {},
}: {
  group: NavGroup;
  collapsed: boolean;
  expandSidebar: () => void;
  hasRole: (r: AdminRole) => boolean;
  badges?: Record<string, number>;
}) {
  const location = useLocation();
  const isActive = group.paths.some((p) => location.pathname.startsWith(p));
  const [open, setOpen] = useState(isActive);

  useEffect(() => {
    if (isActive) setOpen(true);
  }, [isActive]);

  if (!hasRole(group.minRole)) return null;

  const groupBadgeTotal = group.items.reduce((sum, item) => sum + (badges[item.to] ?? 0), 0);

  return (
    <div>
      <button
        onClick={() => {
          if (collapsed) {
            expandSidebar();
          } else {
            setOpen((o) => !o);
          }
        }}
        title={collapsed ? group.label : undefined}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-150
          ${collapsed ? "justify-center" : "justify-between"}
          ${isActive ? "bg-magenta/15 text-magenta font-semibold" : "text-offwhite/60 hover:text-offwhite hover:bg-white/5"}`}
      >
        <span className="flex items-center gap-3">
          <span className="relative flex-shrink-0">
            <group.Icon className="w-4 h-4" />
            {collapsed && groupBadgeTotal > 0 && (
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-yellow" />
            )}
          </span>
          {!collapsed && (
            <span className="flex items-center gap-1.5">
              {group.label}
              {groupBadgeTotal > 0 && (
                <span className="text-xs font-bold min-w-[22px] text-center px-1.5 py-0.5 rounded-md bg-yellow/25 text-yellow leading-none">
                  {groupBadgeTotal > 99 ? "99+" : groupBadgeTotal}
                </span>
              )}
            </span>
          )}
        </span>
        {!collapsed && (
          <FiChevronDown
            className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        )}
      </button>

      {!collapsed && open && <SubItems items={group.items} hasRole={hasRole} badges={badges} />}
    </div>
  );
}

// ── Sidebar ────────────────────────────────────────────────────────────────
export const Sidebar: React.FC = () => {
  const { collapsed, toggle } = useSidebar();
  const { hasRole } = useAdminRole();
  const canModerate = hasRole("MODERATOR");
  const supportStats = useSupportStats(canModerate);
  const moderationStats = useModerationStats(canModerate);

  const subBadges: Record<string, Record<string, number>> = {
    Users: {
      "/verifications": moderationStats.pendingVerifications,
      "/deleted-users": moderationStats.deletionRequests,
    },
    "Content & Safety": {
      "/moderation": moderationStats.pendingMessages,
      "/support": supportStats.open + supportStats.unread,
      "/reports": moderationStats.pendingReports,
    },
  };

  return (
    <aside
      className={`${collapsed ? "w-16" : "w-60"} bg-[#1a1818] border-r border-white/10 flex flex-col flex-shrink-0 transition-all duration-200`}
    >
      {/* Logo + toggle */}
      <div className="flex flex-col border-b border-white/10">
        <div
          className={`px-3 py-2 flex ${collapsed ? "justify-center" : "justify-end"}`}
        >
          <button
            onClick={toggle}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-offwhite/30 hover:text-offwhite hover:bg-white/10 transition-all flex-shrink-0"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <FiChevronRight className="w-4 h-4" />
            ) : (
              <FiChevronLeft className="w-4 h-4" />
            )}
          </button>
        </div>
        <div className="border-t border-white/10" />
        <div
          className={`px-4 py-4 flex ${collapsed ? "justify-center" : "justify-start"}`}
        >
          {collapsed ? (
            <img src={logo} alt="Crays" className="w-8 h-8 object-contain" />
          ) : (
            <img src={logo} alt="Crays" className="w-auto" />
          )}
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-2 py-4 space-y-0.5 overflow-y-auto">
        {/* Dashboard */}
        {hasRole("EDITOR") && (
          <NavLink
            to="/dashboard"
            title={collapsed ? "Dashboard" : undefined}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-150
              ${collapsed ? "justify-center" : ""}
              ${isActive ? "bg-magenta/15 text-magenta font-semibold" : "text-offwhite/60 hover:text-offwhite hover:bg-white/5"}`
            }
          >
            <FiBarChart2 className="w-4 h-4 flex-shrink-0" />
            {!collapsed && "Dashboard"}
          </NavLink>
        )}

        {/* Grouped nav */}
        {navGroups.map((group) => (
          <NavGroupItem
            key={group.label}
            group={group}
            collapsed={collapsed}
            expandSidebar={toggle}
            hasRole={hasRole}
            badges={subBadges[group.label] ?? {}}
          />
        ))}
      </nav>
    </aside>
  );
};
